/**
 * i18n bootstrap — react-i18next 15 + i18next 23.
 *
 * Source : DEC-004 (FR-first, lazy loading namespaces, ICU plurals).
 *
 * Cascade locale (DEC-004 §2.6) :
 *   1. user profile DB (post-auth, hook séparé)
 *   2. localStorage 'labrain.locale'
 *   3. navigator.language → mapping locale supportée
 *   4. fallback 'fr'
 *
 * Namespaces lazy-loaded depuis /public/locales/{lng}/{ns}.json.
 * Wave 1.5+ : path versioning (/locales/v2026.05/{lng}/{ns}.json) sur R2 CDN.
 */
import i18next from 'i18next';
import HttpBackend from 'i18next-http-backend';
import LanguageDetector from 'i18next-browser-languagedetector';
import { initReactI18next } from 'react-i18next';
export const SUPPORTED_LOCALES = ['fr', 'en'];
export const DEFAULT_NAMESPACE = 'common';
export const NAMESPACES = [
    'common',
    'home',
    'onboarding',
    'auth',
    'errors',
    'games',
    'paywall',
    'settings',
    'library',
    'profile',
    'results',
    'session',
    'game-wiki',
];
const fallbackLocale = (import.meta.env.VITE_LOCALE_FALLBACK ?? 'fr');
const defaultLocale = (import.meta.env.VITE_LOCALE_DEFAULT ?? 'fr');
void i18next
    .use(HttpBackend)
    .use(LanguageDetector)
    .use(initReactI18next)
    .init({
    fallbackLng: fallbackLocale,
    lng: defaultLocale,
    supportedLngs: [...SUPPORTED_LOCALES],
    ns: [...NAMESPACES],
    defaultNS: DEFAULT_NAMESPACE,
    interpolation: {
        escapeValue: false, // React already escapes
    },
    backend: {
        loadPath: '/locales/{{lng}}/{{ns}}.json',
    },
    detection: {
        order: ['localStorage', 'navigator', 'htmlTag'],
        lookupLocalStorage: 'labrain.locale',
        caches: ['localStorage'],
    },
    react: {
        useSuspense: true,
    },
    returnNull: false,
    debug: import.meta.env.DEV,
});
export const i18n = i18next;
/**
 * Helper pour aligner serveur (Better-Auth user.locale) ↔ client cache.
 * Appel après login : `await syncLocaleFromUser(user.locale)`.
 */
export async function syncLocaleFromUser(locale) {
    if (!locale)
        return;
    const supported = SUPPORTED_LOCALES.find((l) => l === locale);
    if (!supported)
        return;
    if (i18n.language === supported)
        return;
    await i18n.changeLanguage(supported);
    try {
        window.localStorage.setItem('labrain.locale', supported);
    }
    catch {
        /* storage unavailable */
    }
}
/** Direction RTL si locale RTL (préparation Wave v3+). */
export function getLocaleDirection(locale = i18n.language) {
    const rtlLocales = new Set(['ar', 'he', 'fa', 'ur']);
    return rtlLocales.has(locale.split('-')[0] ?? '') ? 'rtl' : 'ltr';
}
//# sourceMappingURL=i18n.js.map